import type { RefObject } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/** Keep the popover this far from the viewport edge when clamping. */
const VIEWPORT_MARGIN = 8

export interface AnchoredPosition {
  top: number
  left: number
  /**
   * Height budget for the popover, in px. Set only when neither side of the
   * trigger can show it in full — the caller must apply it (with an
   * internal scroll) or the popover will still overflow the viewport.
   */
  maxHeight?: number
}

export interface UseAnchoredPositionOptions {
  /** When true, position is recomputed and the popover is visible. */
  open: boolean
  /** Where to anchor horizontally — defaults to 'left'. */
  align?: 'left' | 'right'
  /** Vertical offset from the trigger in px. Defaults to 4. */
  gap?: number
  /** Approximate width of the popover for right-align math. Required for align='right'. */
  popoverWidth?: number
  /**
   * The popover element itself. Supply it to get viewport-aware placement:
   * the popover flips above the trigger when it would not fit below, and is
   * clamped inside the viewport either way.
   *
   * Without it the popover is always placed below, which silently clips it
   * when the trigger sits low on the page — and because the popover is
   * `position: fixed`, the cut-off part cannot be scrolled to.
   */
  popoverRef?: RefObject<HTMLElement | null>
}

/**
 * Compute viewport-relative coordinates for a popover anchored to a
 * trigger element. Use together with React's `createPortal` to escape
 * parent `overflow: hidden|auto` clipping (table scroll containers,
 * sidebars, etc.).
 *
 * Position is recomputed when the popover opens, on window resize,
 * and on window scroll. For nested scroll containers, callers
 * should also trigger an update when those containers scroll —
 * outside the scope of this hook.
 */
export const useAnchoredPosition = (
  triggerRef: RefObject<HTMLElement | null>,
  { open, align = 'left', gap = 4, popoverWidth = 0, popoverRef }: UseAnchoredPositionOptions,
): AnchoredPosition | null => {
  const [position, setPosition] = useState<AnchoredPosition | null>(null)
  // Whether the popover has been measured since it opened. See the layout
  // effect below — without this the measurement never happens.
  const [measured, setMeasured] = useState(false)

  // The popover's height WITHOUT our own cap applied, and the cap we last
  // emitted.
  //
  // These exist because the element carrying `maxHeight` is the same one we
  // measure. Once a cap lands, offsetHeight reports the CAPPED height — which
  // fits — so re-measuring naively clears the cap, the element springs back to
  // full height, and the next event caps it again. An endless flip, and with
  // the ResizeObserver below attached it is self-sustaining rather than merely
  // scroll-driven.
  //
  // So a measurement is only trusted while the element is uncapped. That still
  // picks up content that grows or shrinks (the reason the observer exists),
  // and simply holds the last good value for as long as we are clamping.
  const naturalHeightRef = useRef<number | null>(null)
  const appliedMaxHeightRef = useRef<number | undefined>(undefined)

  const compute = useCallback(() => {
    // If the trigger DOM node has been unmounted while the popover
    // is still considered open (rare but possible mid-resize or
    // route-transition), clear stale coordinates rather than leaving
    // the last-good rect on screen. The portaled popover would
    // otherwise float at the trigger's last position even though
    // the trigger isn't there anymore.
    if (!triggerRef.current) {
      setPosition(null)
      return
    }
    const rect = triggerRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Zero until the popover has mounted; the layout effect below re-runs
    // this once it has, so the first paint is corrected before it is seen.
    const measuredHeight = popoverRef?.current?.offsetHeight ?? 0
    // Trust a measurement only when the element is NOT being clamped by us:
    // either no cap is applied, or it came back strictly under the cap,
    // which proves the cap is not what is limiting it. Trusting a measure
    // that equals the cap is what caused the cap to fight its own
    // measurement. Refusing every measure while capped was the other
    // extreme: content that SHRANK — the role list narrowing as you type —
    // stayed clamped against a height it no longer had.
    const appliedCap = appliedMaxHeightRef.current
    if (measuredHeight > 0 && (appliedCap === undefined || measuredHeight < appliedCap)) {
      naturalHeightRef.current = measuredHeight
    }
    const popoverHeight = naturalHeightRef.current ?? 0
    const spaceBelow = viewportHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap

    // Flip above only when it genuinely helps: the popover does not fit
    // below AND there is more room above. Otherwise stay below and let the
    // clamp deal with it, so a popover taller than the whole viewport does
    // not end up pinned to the top with its footer off-screen instead.
    // Placement, in the order a user expects:
    //   1. below, if it fits
    //   2. above, if it fits there
    //   3. neither — take the roomier side and CAP the height so the
    //      popover scrolls internally instead of running off the edge
    //
    // Step 3 is the one that actually guarantees the fix. Repositioning
    // alone cannot help a popover taller than the space available, which is
    // why clamping-only still left the list and footer cut off.
    const fitsBelow = popoverHeight === 0 || spaceBelow >= popoverHeight + VIEWPORT_MARGIN
    const fitsAbove = popoverHeight > 0 && spaceAbove >= popoverHeight + VIEWPORT_MARGIN

    let top: number
    let maxHeight: number | undefined

    if (fitsBelow) {
      top = rect.bottom + gap
    } else if (fitsAbove) {
      top = rect.top - gap - popoverHeight
    } else if (spaceAbove > spaceBelow) {
      maxHeight = Math.max(0, spaceAbove - VIEWPORT_MARGIN)
      top = VIEWPORT_MARGIN
    } else {
      maxHeight = Math.max(0, spaceBelow - VIEWPORT_MARGIN)
      top = rect.bottom + gap
    }

    top = Math.max(VIEWPORT_MARGIN, top)

    // Measured width beats the declared one, and is the only width a
    // LEFT-aligned caller has: popoverWidth is documented as required for
    // align='right' and defaults to 0, so relying on it alone skipped the
    // right-edge clamp entirely and let a left-aligned popover near the
    // edge render off-screen.
    const measuredWidth = popoverRef?.current?.offsetWidth ?? 0
    const effectiveWidth = measuredWidth || popoverWidth

    let left = align === 'right' ? rect.right - effectiveWidth : rect.left
    if (effectiveWidth > 0) {
      left = Math.min(left, viewportWidth - effectiveWidth - VIEWPORT_MARGIN)
    }
    left = Math.max(VIEWPORT_MARGIN, left)

    // Bail on an unchanged result. compute() is bound to scroll in capture
    // mode, so allocating a fresh object every event re-rendered the whole
    // consumer on every scroll frame, even while the popover sat still.
    appliedMaxHeightRef.current = maxHeight
    setPosition((prev) =>
      prev && prev.top === top && prev.left === left && prev.maxHeight === maxHeight ? prev : { top, left, maxHeight },
    )
  }, [triggerRef, align, gap, popoverWidth, popoverRef])

  useEffect(() => {
    if (!open) {
      setPosition(null)
      // Next open re-measures from scratch: the content may be different,
      // and the element is unmounted so the cached height means nothing.
      naturalHeightRef.current = null
      appliedMaxHeightRef.current = undefined
      return
    }
    compute()
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)
    return () => {
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [open, compute])

  // Re-measure when the CONTENT changes height, not just the viewport.
  //
  // The measurement below runs once per open, so a popover whose body
  // grows or shrinks while it stays open keeps the flip and the height cap
  // computed from its original size. RoleChips does exactly that: the
  // search box filters the role list as you type, and the Admin hint
  // appears and disappears.
  //
  // Safe against feedback loops because compute() bails when the resulting
  // position is unchanged, so a capped popover settles after one pass.
  // Guarded on the constructor: jsdom does not implement ResizeObserver.
  useEffect(() => {
    const element = popoverRef?.current
    if (!open || !element || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => compute())
    observer.observe(element)
    return () => observer.disconnect()
    // `position` for the same reason as the measurement effect below: the
    // element does not exist on the first pass, and a ref's identity does
    // not change when its .current is populated.
  }, [open, compute, popoverRef, position])

  // Measure the popover once it has actually mounted.
  //
  // Callers render nothing until `position` is set, so on the first pass
  // popoverRef.current is still null and the height reads as 0 — which
  // makes every popover "fit" below and skips both the flip and the height
  // cap. The element only exists from the SECOND render onwards.
  //
  // `measured` is what gets this to re-run. An effect keyed only on
  // [open, compute, popoverRef] cannot: a ref object keeps the same
  // identity when its .current is populated, so the deps are unchanged
  // between those two renders and the effect never fires again. Flipping a
  // piece of state is what schedules the second pass, and it self-limits to
  // one extra measurement per open.
  //
  // Layout effect, so the corrected position lands before paint rather than
  // as a visible jump.
  useLayoutEffect(() => {
    if (!open) {
      if (measured) setMeasured(false)
      return
    }
    if (measured || !popoverRef?.current) return
    setMeasured(true)
    compute()
    // `position` is in the deps because it is what changes between the
    // render where the popover does not exist and the one where it does.
    // Without it nothing schedules this effect again — the ref keeps its
    // identity when .current is populated, so [open, compute, popoverRef]
    // are all unchanged and the measurement never happens. `measured`
    // stops the compute() below from re-triggering this in a loop.
  }, [open, measured, compute, popoverRef, position])

  return position
}
